/**
 * `/workflows/loan-tape/:id`: a loan tape extraction read as a workflow
 * (DOP-481), in the redesigned workflow style (DOP-627).
 *
 * The extraction used to be something you watched happen in a chat and then
 * scrolled back to. As a workflow it is a standing thing: the files it runs
 * on, the tape the last run produced, and every run before it. All three are
 * read out of the same chat (DOP-477), so nothing here can drift from the
 * thread it is made of.
 *
 * The tabs match the other redesigned workflows: Tape (what it produced),
 * Needs you (what to look at before it goes out), Mapping (how each column
 * is made, with the chat's Map graph and SQL as views of it), History, and How it's
 * set up. Links written before DOP-627 (`?tab=data`, `?tab=settings`) are
 * rewritten on load, not dropped.
 *
 * The Tape tab shows ONE run: the latest by default, or whichever run `?run=`
 * names, so a History row can point at an older result without a second
 * screen.
 */

import { Loader2, MessageSquare } from "lucide-react";
import React, {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardShell, {
	SHELL_TAB_PARAM,
} from "../../components/Dashboards/DashboardShell";
import MappingView, {
	isMappingView,
} from "../../components/LoanTape/MappingView";
import ColumnPanel from "../../components/LoanTape/ColumnPanel";
import {
	buildLoanTapeContext,
	loanTapeChipLabel,
} from "../../components/LoanTape/loanTapeChatBridge";
import TapeGlance from "../../components/LoanTape/TapeGlance";
import TapeHistory from "../../components/LoanTape/TapeHistory";
import TapeNeedsYou from "../../components/LoanTape/TapeNeedsYou";
import TapeSetup from "../../components/LoanTape/TapeSetup";
import TapeTable from "../../components/LoanTape/TapeTable";
import RunFailureNote from "../../components/LoanTape/RunFailureNote";
import { explainRunFailure } from "../../components/LoanTape/runFailure";
import {
	ORIGINS,
	ORIGIN_DOT,
	ORIGIN_LABEL,
	unmappedColumns,
	countOrigins,
	loanCountOf,
} from "../../components/LoanTape/loanTapeColumns";
import { formatEpochMs } from "../../components/Monitoring/monitoringFormat";
import type { LoanTapePreviewColumn } from "../../components/Chat/Chat.types";
import type { SchemaExtractorPayload } from "../../components/SchemaExtractorForm";
import { useDashboardsIndex } from "../../context/DashboardsIndex";
import { ApiError } from "../../services/api";
import { useLoanTapeDashboard } from "../../hooks/useLoanTapeDashboard";
import { chatApi } from "../../services/chatApi";
import {
	deleteLoanTapeDashboard,
	previewBlockOf,
	schemaGraphOf,
	workbookBlockOf,
} from "../../services/loanTapeDashboardService";
import { friendlyErrorMessage } from "../../utils/apiErrorDetail";
import {
	LOAN_TAPE_MAPPING_VIEW_PARAM,
	LOAN_TAPE_RUN_PARAM,
	RUN_THREAD_PARAM,
} from "../../utils/chatHref";
import RailFallback from "../../components/Chat/RailFallback";
import { useRailThread } from "../../hooks/useRailThread";

// The rail brings the whole chat page with it; a tape opened to read or
// download should not pay for that.
const LoanTapeAnalystRail = lazy(
	() => import("../../components/LoanTape/LoanTapeAnalystRail"),
);

/** Which run the Tape tab is showing. Absent means "the latest". */
export { LOAN_TAPE_RUN_PARAM };

/** Columns, Map or SQL on the Mapping tab. Absent means Columns. */
const MAPPING_VIEW_PARAM = LOAN_TAPE_MAPPING_VIEW_PARAM;

const TAPE_TAB = "tape";
/** Every tab the page has, in order. */
const TAB_IDS: readonly string[] = [
	TAPE_TAB,
	"review",
	"mapping",
	"history",
	"setup",
];

/** Where a `?tab=` written before DOP-627 lands now. */
const LEGACY_TABS: Record<string, string> = {
	data: TAPE_TAB,
	settings: "setup",
};

const PRIMARY =
	"inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#004C4B] px-3.5 text-sm font-semibold text-white hover:bg-[#003a3a] disabled:opacity-60";
const SECONDARY =
	"inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-[#1F2937] hover:bg-gray-50 disabled:opacity-60";

const saveBlob = (blob: Blob, filename: string): void => {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
};

const LoanTapeDashboardPage: React.FC = () => {
	const { id = "" } = useParams();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { refresh: refreshIndex } = useDashboardsIndex();
	const { dashboard, loading, error, isRunning, run } =
		useLoanTapeDashboard(id);
	const [editing, setEditing] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [downloadError, setDownloadError] = useState<string | null>(null);
	const [inspected, setInspected] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);
	const [deleteError, setDeleteError] = useState<string | null>(null);

	// Rewrite an older link into the tab it would be written with today.
	// Replaced, not pushed, so Back does not bounce off the old shape.
	const legacyTab = LEGACY_TABS[searchParams.get(SHELL_TAB_PARAM) ?? ""];
	useEffect(() => {
		if (!legacyTab) return;
		const next = new URLSearchParams(searchParams);
		next.set(SHELL_TAB_PARAM, legacyTab);
		setSearchParams(next, { replace: true });
	}, [legacyTab, searchParams, setSearchParams]);

	const requestedRun = Number(searchParams.get(LOAN_TAPE_RUN_PARAM));
	const mappingView = searchParams.get(MAPPING_VIEW_PARAM);
	// A `?run=` naming a run this thread does not have reads as the latest:
	// the link is stale, the workflow is not.
	const shown =
		dashboard?.runs.find((entry) => entry.id === requestedRun) ??
		dashboard?.latest ??
		null;

	const preview = previewBlockOf(shown);
	const workbook = workbookBlockOf(shown);
	const graph = schemaGraphOf(shown);
	const columns = preview?.columns ?? [];
	const rows = preview?.rows ?? [];
	const toCheck = unmappedColumns(columns);
	const counts = countOrigins(columns);
	const loans = loanCountOf(workbook, shown?.blocks);
	const failed = shown?.status === "failed";
	// The run's own files, not the workflow's newest: an older failed run is
	// explained against what it was given.
	const failure = explainRunFailure(
		shown?.error ?? null,
		shown?.config ?? dashboard?.config ?? null,
	);
	const inspectedColumn =
		columns.find((column) => column.header === inspected) ?? null;

	const setTab = useCallback(
		(tab: string, extra?: (next: URLSearchParams) => void) => {
			const next = new URLSearchParams(searchParams);
			next.set(SHELL_TAB_PARAM, tab);
			extra?.(next);
			setSearchParams(next);
		},
		[searchParams, setSearchParams],
	);

	const showRun = useCallback(
		(runId: number) => {
			setInspected(null);
			setTab(TAPE_TAB, (next) =>
				next.set(LOAN_TAPE_RUN_PARAM, String(runId)),
			);
		},
		[setTab],
	);

	const openColumn = useCallback(
		(column: LoanTapePreviewColumn) => {
			setInspected(column.header);
			setTab(TAPE_TAB);
		},
		[setTab],
	);

	const handleDownload = useCallback(async () => {
		if (!workbook) return;
		setDownloadError(null);
		setDownloading(true);
		try {
			const blob = await chatApi.downloadWorkbookArtifact(id, workbook.block_id);
			saveBlob(blob, workbook.filename);
		} catch (caught) {
			setDownloadError(
				friendlyErrorMessage(caught, "Could not download the Excel file."),
			);
		} finally {
			setDownloading(false);
		}
	}, [id, workbook]);

	const runAgain = useCallback(
		(payload?: SchemaExtractorPayload) => {
			setInspected(null);
			// The new run becomes the one to look at, so drop any pinned older one.
			setTab(TAPE_TAB, (next) => next.delete(LOAN_TAPE_RUN_PARAM));
			void run(payload);
		},
		[run, setTab],
	);

	const handleConfigSubmit = useCallback(
		(payload: SchemaExtractorPayload) => {
			setEditing(false);
			runAgain(payload);
		},
		[runAgain],
	);

	const handleDelete = useCallback(async () => {
		setDeleteError(null);
		setDeleting(true);
		try {
			await deleteLoanTapeDashboard(id);
			// The index is the sidebar's list too, so it is refreshed before the
			// navigation: landing on /workflows beside a row that no longer
			// exists would read as the delete having failed.
			refreshIndex();
			navigate("/workflows");
		} catch (caught) {
			setDeleting(false);
			setDeleteError(
				// A run that started between the page's last read and the click:
				// the server refuses rather than deleting a live generation, and
				// "wait" is the whole of what the user has to do about it.
				caught instanceof ApiError && caught.status === 409
					? "This loan tape is running. Wait for the run to finish."
					: friendlyErrorMessage(caught, "Could not delete this loan tape."),
			);
		}
	}, [id, navigate, refreshIndex]);

	// ---- Chat ----------------------------------------------------------------
	// The URL names the open thread, so a reload lands back on it and a thread
	// opened from the sidebar opens the rail.
	const {
		threadId: threadParam,
		open: chatOpen,
		setOpen: setChatOpen,
		nameThread,
		close: closeChat,
	} = useRailThread(RUN_THREAD_PARAM);
	// A `?tab=` naming a tab this page does not have reads as the Tape tab,
	// which is what the shell shows for it.
	const requestedTab = searchParams.get(SHELL_TAB_PARAM) ?? TAPE_TAB;
	const activeTab = TAB_IDS.includes(requestedTab) ? requestedTab : TAPE_TAB;
	// Read at send time, so a turn carries the run, tab and column on screen
	// at that moment, not the ones the rail was opened on. A thread is about
	// the tape; this is what says which run each question is about.
	const buildChatContext = useCallback(
		() =>
			buildLoanTapeContext({
				tapeId: id,
				run: shown,
				tab: activeTab,
				mappingView: isMappingView(mappingView) ? mappingView : "columns",
				column:
					activeTab === TAPE_TAB ? (inspectedColumn?.header ?? null) : null,
			}),
		[id, shown, activeTab, mappingView, inspectedColumn],
	);

	const meta = [
		loans !== null ? `${loans.toLocaleString()} loans` : null,
		preview ? `${columns.length} columns` : null,
		shown
			? isRunning
				? "Building now"
				: `Last built ${formatEpochMs(shown.started_at_ms)}`
			: "Never built",
	]
		.filter(Boolean)
		.join(" · ");

	const needsYou = toCheck.length + (failed ? 1 : 0);
	const tabs = [
		{ id: TAPE_TAB, label: "Tape" },
		{
			id: "review",
			label: "Needs you",
			...(needsYou > 0
				? { count: needsYou, countTone: "attention" as const }
				: {}),
		},
		{ id: "mapping", label: "Mapping" },
		{ id: "history", label: "History" },
		{ id: "setup", label: "How it's set up" },
	];

	// The panel docks beside the Tape tab only; on any other tab it would be a
	// column of a table that is not on screen.
	const onTapeTab = activeTab === TAPE_TAB;

	const renderTape = () => (
		<div className="flex flex-col gap-3.5">
			{isRunning && (
				<p className="flex items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#1F2937]">
					<Loader2 className="h-4 w-4 animate-spin text-[#004C4B]" aria-hidden />
					Building the loan tape. It shows here when the run finishes.
				</p>
			)}
			{failed && (
				<div
					role="alert"
					className="rounded-xl border border-[#fecdca] bg-[#fffbfa] px-4 py-3 text-[#b42318]"
				>
					<RunFailureNote failure={failure} />
					{dashboard?.config && !isRunning && (
						<button
							type="button"
							onClick={() => {
								setEditing(true);
								setTab("setup");
							}}
							className={`${SECONDARY} mt-2.5`}
						>
							Change the files
						</button>
					)}
				</div>
			)}
			{toCheck.length > 0 && (
				<div className="flex items-center gap-3 rounded-xl border border-[#fedf89] bg-[#fffcf2] px-4 py-3">
					<span className="text-sm text-[#1F2937]">
						<b>
							{toCheck.length === 1
								? "1 unmapped column"
								: `${toCheck.length} unmapped columns`}
						</b>{" "}
						before you send this out: Doppler couldn't find{" "}
						{toCheck.length === 1 ? "its source" : "their source"} in the new
						file.
					</span>
					<button
						type="button"
						onClick={() => setTab("review")}
						className={`${PRIMARY} ml-auto`}
					>
						Review
					</button>
				</div>
			)}
			{preview ? (
				<>
					<TapeGlance loans={loans} columns={columns.length} counts={counts} />
					<div className="flex flex-wrap items-center gap-4 text-xs text-[#5B6472]">
						<span>Under each column, where it came from:</span>
						{ORIGINS.map((origin) => (
							<span key={origin} className="inline-flex items-center gap-1.5">
								<span
									aria-hidden
									className={`inline-block h-[7px] w-[7px] rounded-full ${ORIGIN_DOT[origin]}`}
								/>
								{ORIGIN_LABEL[origin]}
							</span>
						))}
						<span className="ml-auto">Click any column to see how it was made</span>
					</div>
					<TapeTable
						columns={columns}
						rows={rows}
						selected={inspected}
						onSelect={(column) =>
							setInspected((current) =>
								current === column.header ? null : column.header,
							)
						}
					/>
					<p className="text-xs text-[#6B7280]">
						Showing the first {rows.length}{" "}
						{loans !== null ? `of ${loans.toLocaleString()} loans` : "loans"}.
						Download the Excel file for the whole tape.
					</p>
				</>
			) : (
				!isRunning && (
					<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]">
						No loan tape yet. Run this workflow to build one.
					</p>
				)
			)}
		</div>
	);

	const renderTab = (tab: string) => {
		if (loading && !dashboard) {
			return (
				<p className="flex items-center gap-2 text-sm text-[#5B6472]">
					<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
					Loading…
				</p>
			);
		}

		switch (tab) {
			case "review":
				return (
					<TapeNeedsYou
						failure={failed ? failure : null}
						columns={toCheck}
						canRun={!isRunning && Boolean(dashboard?.config)}
						onRun={() => runAgain()}
						onChangeFiles={() => {
							setEditing(true);
							setTab("setup");
						}}
						onOpenColumn={openColumn}
					/>
				);
			case "mapping":
				return (
					<MappingView
						view={isMappingView(mappingView) ? mappingView : "columns"}
						onViewChange={(view) =>
							setTab("mapping", (next) => next.set(MAPPING_VIEW_PARAM, view))
						}
						columns={columns}
						graph={graph}
						onOpenColumn={openColumn}
						loans={loans}
						// The run's own files: an older run is drawn with what it
						// was given, not with the tape's newest setup.
						sourceName={(shown?.config ?? dashboard?.config)?.new_source.filename}
						exampleName={
							(shown?.config ?? dashboard?.config)?.training_source.filename
						}
						sourceLabel={(shown?.config ?? dashboard?.config)?.new_source.deal_name}
						exampleLabel={
							(shown?.config ?? dashboard?.config)?.training_source.deal_name
						}
					/>
				);
			case "history":
				return (
					<TapeHistory
						runs={dashboard?.runs ?? []}
						selectedRunId={shown?.id ?? null}
						onOpenRun={showRun}
					/>
				);
			case "setup":
				return (
					<TapeSetup
						config={dashboard?.config ?? null}
						editing={editing}
						canEdit={!isRunning}
						canDelete={!isRunning}
						onEdit={() => setEditing(true)}
						onCancel={() => setEditing(false)}
						onSubmit={handleConfigSubmit}
						onDelete={() => void handleDelete()}
						deleting={deleting}
					/>
				);
			default:
				return renderTape();
		}
	};

	return (
		<DashboardShell
			title={dashboard?.title ?? "Loan tape"}
			meta={meta}
			tabs={tabs}
			actions={
				<>
					<button
						type="button"
						onClick={() => runAgain()}
						disabled={isRunning || !dashboard?.config}
						className={SECONDARY}
					>
						{isRunning && (
							<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
						)}
						{isRunning ? "Running" : "Run again"}
					</button>
					<button
						type="button"
						onClick={() => void handleDownload()}
						disabled={!workbook || downloading}
						className={PRIMARY}
					>
						Download Excel
					</button>
				</>
			}
			tabActions={
				<button
					type="button"
					onClick={() => (chatOpen ? closeChat() : setChatOpen(true))}
					aria-pressed={chatOpen}
					className={`flex items-center gap-1.5 rounded border px-2 py-1 text-sm ${
						chatOpen
							? "border-[#008782] bg-[#E6F2F1] text-[#004C4B]"
							: "border-gray-300 text-text-base hover:bg-gray-50"
					}`}
				>
					<MessageSquare className="h-4 w-4" aria-hidden="true" />
					Chat
				</button>
			}
			rightRail={
				// One panel at a time: two beside the tape leave no room for the
				// tape. While Chat is open the selected column stays marked in
				// the table and named on the chip, and its panel comes back when
				// Chat closes.
				chatOpen ? (
					<Suspense fallback={<RailFallback />}>
						<LoanTapeAnalystRail
							tapeId={id}
							contextLabel={loanTapeChipLabel({
								run: shown,
								isLatest: shown?.id === dashboard?.latest?.id,
								startedLabel: shown ? formatEpochMs(shown.started_at_ms) : null,
								column: onTapeTab ? (inspectedColumn?.header ?? null) : null,
							})}
							buildContext={buildChatContext}
							prefill={null}
							onClose={closeChat}
							initialChatId={threadParam}
							onThreadChange={nameThread}
						/>
					</Suspense>
				) : onTapeTab && inspectedColumn ? (
					<ColumnPanel
						column={inspectedColumn}
						columns={columns}
						rows={rows}
						onClose={() => setInspected(null)}
					/>
				) : undefined
			}
		>
			{(tab) => (
				<div className="space-y-4">
					{(error || downloadError || deleteError) && (
						<p role="alert" className="text-sm text-[#b42318]">
							{error ?? downloadError ?? deleteError}
						</p>
					)}
					{renderTab(tab)}
				</div>
			)}
		</DashboardShell>
	);
};

export default LoanTapeDashboardPage;
